import apiClient from "./apiClient";

export interface TravelSegment {
  _id: string;
  travelId: string;
  segmentTitle: string;
  segmentType: "transfer" | "destination" | "accommodation" | "activity";
  segmentOperator?: string;
  bookingReference?: string;
  startDateTime: string;
  startLocation: string;
  startCountry: string;
  endDateTime: string;
  endLocation: string;
  endCountry: string;
  duration?: number;
  transportationType?: "flight" | "train" | "bus" | "car" | "ship" | "other";
  accommodationType?:
    | "hotel"
    | "hostel"
    | "apartment"
    | "cruise_cabin"
    | "other";
  healthRisk?: {
    vectorBorneDiseases?: string[];
    foodWaterSafety?: "low" | "medium" | "high";
    airQualityIndex?: number;
    altitudeMeters?: number;
    climateRisks?: string[];
  };
  healthServices?: {
    medicalFacilities?: string[];
    emergencyContact?: string;
    pharmacyLocations?: string[];
  };
  healthRequirements?: {
    requiredVaccinations?: string[];
    recommendedVaccinations?: string[];
    malariaProphylaxis?: boolean;
  };
  notes?: string;
  documents?: string[];
  healthStatus?: "ready" | "pending" | "warning";
  createdAt: string;
  updatedAt: string;
}

export interface Travel {
  _id: string;
  userId: string;
  tripTitle?: string;
  destination?: string;
  country?: string;
  departureDate?: string;
  returnDate?: string;
  purpose?: string;
  tripType?:
    | "cruise"
    | "beach"
    | "mountains"
    | "sports"
    | "education"
    | "event"
    | "culture"
    | "expedition"
    | "other";
  tripOperator?: string;
  bookingReference?: string;
  startLocation?: string;
  endLocation?: string;
  duration?: number;
  companions?: number;
  notes?: string;
  status:
    | "draft"
    | "planned"
    | "current"
    | "completed"
    | "cancelled"
    | "archived";
  // Both are written by POST/PATCH /api/travels and returned on every trip
  // (travels-fastify.ts), but were missing here - so TravelDetailPage had to
  // cast them and could not be type-checked against the real document.
  statusMode?: "automatic" | "manual";
  activities?: string[];
  segments?: string[];
  healthRequirements?: string[];
  healthWarnings?: string[];
  healthRecommendations?: string[];
  vaccinations?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateTravelData {
  tripTitle?: string;
  destination: string;
  country: string;
  departureDate: string;
  returnDate: string;
  purpose?: string;
  tripType?: string;
  tripOperator?: string;
  bookingReference?: string;
  startLocation?: string;
  endLocation?: string;
  companions?: number;
  notes?: string;
}

export interface UpdateTravelData {
  tripTitle?: string;
  destination?: string;
  country?: string;
  departureDate?: string;
  returnDate?: string;
  purpose?: string;
  tripType?: string;
  tripOperator?: string;
  bookingReference?: string;
  startLocation?: string;
  endLocation?: string;
  companions?: number;
  notes?: string;
  status?: string;
}

export interface CreateSegmentData {
  segmentTitle: string;
  segmentType: string;
  segmentOperator?: string;
  bookingReference?: string;
  startDateTime: string;
  startLocation: string;
  startCountry: string;
  endDateTime: string;
  endLocation: string;
  endCountry: string;
  transportationType?: string;
  accommodationType?: string;
  notes?: string;
}

export interface UpdateSegmentData {
  segmentTitle?: string;
  segmentType?: string;
  segmentOperator?: string;
  bookingReference?: string;
  startDateTime?: string;
  startLocation?: string;
  startCountry?: string;
  endDateTime?: string;
  endLocation?: string;
  endCountry?: string;
  transportationType?: string;
  accommodationType?: string;
  healthStatus?: string;
  notes?: string;
}

/**
 * Fetch all travels for the authenticated user
 */
export const getTravels = async (): Promise<Travel[]> => {
  const response = await apiClient.get("/travels");
  return response.data.data.travels;
};

/**
 * Fetch a single travel by ID
 */
export const getTravel = async (id: string): Promise<Travel> => {
  const response = await apiClient.get(`/travels/${id}`);
  return response.data.data.travel;
};

/**
 * Create a new travel
 */
export const createTravel = async (data: CreateTravelData): Promise<Travel> => {
  const response = await apiClient.post("/travels", data);
  return response.data.data.travel;
};

/**
 * Update an existing travel
 */
export const updateTravel = async (
  id: string,
  data: UpdateTravelData,
): Promise<Travel> => {
  const response = await apiClient.put(`/travels/${id}`, data);
  return response.data.data.travel;
};

/**
 * Delete a travel
 */
export const deleteTravel = async (id: string): Promise<void> => {
  await apiClient.delete(`/travels/${id}`);
};

/**
 * Archive a travel
 */
export const archiveTravel = async (id: string): Promise<Travel> => {
  return updateTravel(id, { status: "archived" });
};

// ============ SEGMENT API FUNCTIONS ============

/**
 * Fetch all segments for a travel
 */
export const getSegments = async (
  travelId: string,
): Promise<TravelSegment[]> => {
  const response = await apiClient.get(`/travels/${travelId}/segments`);
  return response.data.data.segments;
};

/**
 * Fetch a single segment by ID
 */
export const getSegment = async (
  travelId: string,
  segmentId: string,
): Promise<TravelSegment> => {
  const response = await apiClient.get(
    `/travels/${travelId}/segments/${segmentId}`,
  );
  return response.data.data.segment;
};

/**
 * Create a new segment
 */
export const createSegment = async (
  travelId: string,
  data: CreateSegmentData,
): Promise<TravelSegment> => {
  const response = await apiClient.post(`/travels/${travelId}/segments`, data);
  return response.data.data.segment;
};

/**
 * Update an existing segment
 */
export const updateSegment = async (
  travelId: string,
  segmentId: string,
  data: UpdateSegmentData,
): Promise<TravelSegment> => {
  const response = await apiClient.put(
    `/travels/${travelId}/segments/${segmentId}`,
    data,
  );
  return response.data.data.segment;
};

/**
 * Delete a segment
 */
export const deleteSegment = async (
  travelId: string,
  segmentId: string,
): Promise<void> => {
  await apiClient.delete(`/travels/${travelId}/segments/${segmentId}`);
};

// ============ COMPANION API FUNCTIONS ============

export interface TravelCompanion {
  _id: string;
  travelId: string;
  invitedByUserId: string;
  invitedEmail: string;
  invitedUserId?: string;
  status: "pending" | "accepted" | "declined";
  invitedAt: string;
  respondedAt?: string;
}

export const getCompanions = async (
  travelId: string,
): Promise<TravelCompanion[]> => {
  const response = await apiClient.get(`/travels/${travelId}/companions`);
  return response.data.data.companions;
};

export const inviteCompanion = async (
  travelId: string,
  email: string,
): Promise<TravelCompanion> => {
  const response = await apiClient.post(`/travels/${travelId}/companions`, {
    email,
  });
  return response.data.data.companion;
};

export const updateCompanionStatus = async (
  travelId: string,
  companionId: string,
  status: "accepted" | "declined",
): Promise<TravelCompanion> => {
  const response = await apiClient.patch(
    `/travels/${travelId}/companions/${companionId}`,
    { status },
  );
  return response.data.data.companion;
};

export const removeCompanion = async (
  travelId: string,
  companionId: string,
): Promise<void> => {
  await apiClient.delete(`/travels/${travelId}/companions/${companionId}`);
};

// ============ SEGMENT DOCUMENTS ============

export interface SegmentDocument {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
  category: "ticket" | "booking" | "voucher" | "insurance" | "other";
  uploadedAt: string;
  url: string;
}

export const uploadSegmentDocument = async (
  travelId: string,
  segmentId: string,
  file: File,
  category: SegmentDocument["category"],
): Promise<SegmentDocument> => {
  const formData = new FormData();
  formData.append("document", file);
  formData.append("category", category);
  const response = await apiClient.post(
    `/travels/${travelId}/segments/${segmentId}/documents`,
    formData,
  );
  return response.data.data.document;
};

export const deleteSegmentDocument = async (
  travelId: string,
  segmentId: string,
  documentId: string,
): Promise<void> => {
  await apiClient.delete(
    `/travels/${travelId}/segments/${segmentId}/documents/${documentId}`,
  );
};

// ============ TRIP HEALTH SUMMARY (PREMIUM) ============

export interface TripSummaryCounts {
  medications: number;
  vitalSigns: number;
  labResults: number;
  immunizations: number;
  conditions: number;
  allergies: number;
}

export interface TripSummaryPreview {
  counts: TripSummaryCounts;
  empty: boolean;
  bundle: { total: number };
}

/**
 * What would go to a doctor, without sending it.
 *
 * Fetched before the form is shown so the traveller can see the extent of the
 * disclosure first — this is the one screen in the app that hands health data
 * to somebody outside it.
 */
export const getTripHealthSummary = async (
  travelId: string,
): Promise<TripSummaryPreview> => {
  const response = await apiClient.get(`/travels/${travelId}/health-summary`);
  return response.data.data;
};

export const sendTripHealthSummary = async (
  travelId: string,
  input: { recipientEmail: string; recipientName?: string; note?: string },
): Promise<{ sentTo: string; resources: number }> => {
  const response = await apiClient.post(
    `/travels/${travelId}/health-summary/send`,
    input,
  );
  return response.data.data;
};

// ============ TRAVEL ADVISORY WATCH (PREMIUM) ============

export interface AdvisoryChange {
  kind: string;
  summary: string;
  from?: string;
  to?: string;
}

export interface AdvisoryHistory {
  checkedAt: string | null;
  destinationCountry?: string;
  state: {
    riskLevel: string;
    requiredVaccinations: string[];
    outbreaks: { disease: string; severity: string }[];
  } | null;
  history: { at: string; changes: AdvisoryChange[] }[];
}

/**
 * What has changed about this destination since the trip was added.
 *
 * The watcher notifies once per change, which is right at the moment and no
 * use a week later; this is the record of what the traveller was told.
 */
export const getAdvisoryChanges = async (
  travelId: string,
): Promise<AdvisoryHistory> => {
  const response = await apiClient.get(`/travels/${travelId}/advisory-changes`);
  return response.data.data;
};

// ============ THE TRAVELLER'S OWN DOCTOR (PREMIUM) ============

export interface DoctorContact {
  email: string;
  name: string | null;
  updatedAt: string;
}

export const getDoctorContact = async (): Promise<DoctorContact | null> => {
  const response = await apiClient.get("/travels/doctor-contact");
  return response.data.data.doctor;
};

export const saveDoctorContact = async (input: {
  email: string;
  name?: string;
}): Promise<DoctorContact> => {
  const response = await apiClient.put("/travels/doctor-contact", input);
  return response.data.data.doctor;
};

export const forgetDoctorContact = async (): Promise<void> => {
  await apiClient.delete("/travels/doctor-contact");
};
